// ──────────────────────────────────────────────
// GeoDrops — Pins Data Hook
// Manages loading, refreshing, and mutating pins.
// ──────────────────────────────────────────────

import { useState, useCallback, useRef } from "react";
import type { Pin, CreatePinInput, PinCategory } from "@/types";
import { fetchNearbyPins, fetchMyPins, createPin, deletePin, ApiError } from "@/lib/api";

interface UsePinsResult {
  pins: Pin[];
  myPins: Pin[];
  loading: boolean;
  error: string | null;
  loadNearby: (lat: number, lng: number, category?: PinCategory) => Promise<void>;
  loadMyPins: () => Promise<void>;
  addPin: (input: CreatePinInput) => Promise<Pin>;
  removePin: (id: string) => Promise<void>;
}

export function usePins(): UsePinsResult {
  const [pins, setPins] = useState<Pin[]>([]);
  const [myPins, setMyPins] = useState<Pin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const loadNearby = useCallback(async (lat: number, lng: number, category?: PinCategory) => {
    // Cancel any in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const data = await fetchNearbyPins(lat, lng, 2000, category);
      setPins(data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load nearby pins");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyPins = useCallback(async () => {
    try {
      const data = await fetchMyPins();
      setMyPins(data);
    } catch (err) {
      console.error("Failed to load my pins:", err);
    }
  }, []);

  const addPin = useCallback(async (input: CreatePinInput): Promise<Pin> => {
    const pin = await createPin(input);
    // Optimistically add to both lists
    setPins((prev) => [pin, ...prev]);
    setMyPins((prev) => [pin, ...prev]);
    return pin;
  }, []);

  const removePin = useCallback(async (id: string) => {
    await deletePin(id);
    // Optimistically remove from both lists
    setPins((prev) => prev.filter((p) => p.id !== id));
    setMyPins((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return { pins, myPins, loading, error, loadNearby, loadMyPins, addPin, removePin };
}
